import React from 'react';
import c from 'classnames';
import { history } from 'instantsearch.js/es/lib/routers';

import { SearchkitWrapper } from '@uncinc/react-searchkit';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../config/constants';
import { hitsPerPage, resultAttributes, sorting } from './config';

// Subcomponents.
import GoToLastPage from './GoToLastPage';
import NavigateHandle, { type NavigateHandleRef } from './NavigateHandle';
import Results from './Results';
import Pagination from './Pagination';
import Stats from './Stats';
import RefreshTrigger, { type RefreshTriggerHandleRef } from './RefreshTrigger';

interface SearchForumThreadSubcomponents {
  Results: typeof Results;
  Pagination: typeof Pagination;
  Stats: typeof Stats;
  RefreshTrigger: typeof RefreshTrigger;
  NavigateHandle: typeof NavigateHandle;
}

export interface SearchForumThreadProps extends Omit<SearchkitWrapperProps, 'connection' | 'indexName' | 'routing' | 'analytics' | 'searchSettings' | 'requestOptions'> {
  nid: number;
}

// eslint-disable-next-line max-len
const SearchForumThread: React.FC<SearchForumThreadProps> & SearchForumThreadSubcomponents = (props) => {
  const {
    nid,
    className,
    children,
    ...restProps
  } = props;

  const classNames = c('SearchForumThread', className);

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={APP_OPTIONS.parameters.searchkitIndexForumThread}
      routing={{
        router: history(),
        stateMapping: {
          // This makes sure the query param for page is not
          // ?elasticsearch_index_zelfhulpalcohol_forum_thread%5Bpage%5D=1 for example,
          // but ?page=1 instead.
          //
          // @ts-expect-error - This is the correct way, but ts doesn't expect it.
          stateToRoute: (uiState) => {
            const indexUiState = uiState[APP_OPTIONS.parameters.searchkitIndexForumThread];

            return {
              page: indexUiState.page,
            };
          },
          // @ts-expect-error - This is the correct way, but ts doesn't expect it.
          routeToState: (routeState) => ({
            [APP_OPTIONS.parameters.searchkitIndexForumThread]: {
              page: routeState.page,
            },
          }),
        },
      }}
      deepPagination
      analytics={false}
      hitsPerPage={hitsPerPage}
      searchSettings={{
        search_attributes: [],
        result_attributes: resultAttributes,
        sorting,
      }}
      requestOptions={{
        getBaseFilters: () => [
          {
            bool: {
              must: [
                {
                  term: {
                    comment_post_id: { value: nid },
                  },
                },
                {
                  term: {
                    comment_status: { value: true },
                  },
                },
              ],
            },
          },
        ],
      }}
    >
      <GoToLastPage />
      {children}
    </SearchkitWrapper>
  );
};

SearchForumThread.Results = Results;
SearchForumThread.Pagination = Pagination;
SearchForumThread.Stats = Stats;
SearchForumThread.RefreshTrigger = RefreshTrigger;
SearchForumThread.NavigateHandle = NavigateHandle;

export type { RefreshTriggerHandleRef, NavigateHandleRef };
export default SearchForumThread;
